import {
    PreloadOptions,
    ScreenPluginOptions,
    StageOptions,
} from '@goodboydigital/astro';

import { LoadingScreen } from './screens/LoadingScreen';
import { NoTransition } from './screens/transitions/NoTransition';
import { QuickAlphaTransition } from './screens/transitions/QuickAlphaTansition';
import { SkyTransition } from './screens/transitions/SkyTransition';
import { Browser } from './utils/Browser';

interface AstroAppConfig
{
    stage: StageOptions
    screens: ScreenPluginOptions
    preload: PreloadOptions
}

/**
 * Astro application config
 */
const AppConfig = {
    screens: {
        main: {
            loaderScreenClass: LoadingScreen,
            defaultTransitionClass: NoTransition
        },
        overlay: {
            loaderScreenClass: LoadingScreen,
            defaultTransitionClass: QuickAlphaTransition,
            defaultTransitionOptions: { duration: 0.3 },
        },
    },
    preload: {
        loaderScreenClass: LoadingScreen,
    },
    stage: {
        alwaysOnAccessibility: false,
        accessibilityDebug: false,
        backgroundColor: 0x4fc9f6,
        clearBeforeRender: true,
        resolution: 1,
        antialias: false,
    },
} as AstroAppConfig;

/**
 * General app & game settings that can be changed through url params
 */
const Settings = {
    /** App target fps */
    fps: 60,

    /** Initial app language */
    language: '',

    /** Assets quality */
    quality: 'high',

    /** Game debug mode */
    debug: false,

    /** Recording mode, for creating ghost data */
    rec: false,

    /** Uncrashable mode, only for testing */
    god: false,

    /** Cull physics bodies, for performance tests */
    physicsCulling: false,

    /** Go straight to Spine debug scene */
    spineDebug: false,

    /** Go straight to gameplay */
    fastplay: false,

    /** Camera mode, for testing */
    camera: 'normal' as 'normal'|'simple'|'static',

    /** Randomise chassis, wheels, colors and character */
    randomGear: false,

    /** Force a specific chunk, for testing */
    chunk: '',

    /** Force a specific level, for testing */
    level: '',

    /** Force a specific race index, for testing */
    race: -1,

    /** Force rank points, for testing */
    rank: -1,

    /** Force number of coins, for testing */
    coins: -1,

    /** Force number of diamonds, for testing */
    diamonds: -1,

    /** Turn on shop debug mode */
    shopDebug: false,

    /** Turn ads on/off */
    noads: false,

    /** Starting screen of the app */
    to: 'splash',

    /** Reset all persistent data */
    reset: false,

    /** Reset shop purchases persistent data */
    resetShop: false,

    /** Text debug mode */
    textDebug: false,

    /** Ground collision should cause crash? */
    groundCrash: false,

    /** Num races can be infinite, but that can be capped */
    maxRaces: 9999,

    /** Allow rewards while game is running in browser */
    browserRewards: false,
    
    /** Strip out a bunch of content for a lite web version */
    browserLite: false,

    /** Number of coins to reward per trick combo level */
    trickCoins: 5,

    /** Auto accept terms, nickname and flag, for debug */
    autoAccept: false,
    
    /** Allow claiming daily reward even if claimed before */
    overrideDailyRewardClaim: false,
    
    /** Unlock all customisation/characters in shop */
    unlockAllShopContent: false,
    
    isApp: () => {
        return window.cordova;
    },
};

/**
 * Override settings properties by the ones found in given object
 * Properties will be overriden only if types are matching
 */
function overrideSettings(params: Record<string, any>): void
{
    for (const k in params)
    {
        // Ensure that Settings have the same property
        // eslint-disable-next-line no-prototype-builtins
        if (Settings.hasOwnProperty(k))
        {
            // Ensure that param type matches the same Settings property type
            const settingType = typeof (Settings as any)[k];

            if (settingType === 'number')
            {
                const v = Number(params[k]);

                if (!isNaN(v)) (Settings as any)[k] = v;
            }
            else if (settingType === 'string')
            {
                const v = String(params[k]);

                if (v) (Settings as any)[k] = v;
            }
            else if (settingType === 'boolean')
            {
                const v = String(params[k]).toLowerCase();

                if (v === '') (Settings as any)[k] = true;
                if (v === 'true') (Settings as any)[k] = true;
                if (v === 'false') (Settings as any)[k] = false;
            }
        }
    }
}

if(_MODE_ != 'production')
{
    overrideSettings(Browser.getUrlParams());
}

if(_BROWSER_LITE_)
{
    Settings.browserLite = true;
}

export { AppConfig, Settings };
